import { niceNumber } from "./Util";
import * as tm from "./backend/models.generated.types";
import * as t from "./backend/manufacturing2.generated.types";

export type ModelData = {
    id: string;
    isDisabled: boolean;
    materialId: string;
    materialUuid: string;
    materialTitle: string;
    lineId: string;
    lineUuid: string;
    lineTitle: string;
    processNum: string;
    modelSpeed: number;
    modelSetup: number;
    normativeSpeed: number;
    normativeSetup: number;
    historicalData: { quantity: number; duration: number }[];
    latestOperations: Operation[];
    modelFallbackThresholdH: number;
    skipTooShortOrderFallbackToNormative: boolean;
    skipTooOptimisticFallbackToNormative: boolean;
    ignorePredictionModelForLine: boolean;
    ignorePredictionModelForMaterial: boolean;
};

export type Operation = {
    operationUuid: string;
    orderExternalId: string;
    actualDateStartTs: number | null;
    actualDateEndTs: number | null;
    actualDurationH: number;
    quantity: number;
    breakTime?: number;
};

type GenerateModelDataParams = {
    modelInfo: tm.IGetSingleModelRes;
    points: tm.IGetSingleModelInputDataRes;
    norms: tm.IModelSummaryNorms | null;
    evaluatedOperations: tm.IEvaluateModelForOperationResult[];
    material: t.IMaterialModel;
};

/** Generates a data structure used in the model details page
 * based on 4 different data sources.
 */
export const generateModelData = ({
    modelInfo,
    points,
    norms,
    evaluatedOperations,
    material,
}: GenerateModelDataParams): ModelData => {
    const norm_base_units = norms?.norm_base_units ?? 0;
    const norm_machine_time = norms?.norm_machine_time;
    const safe_norm_machine_time = norm_machine_time != null && norm_machine_time > 0 ? norm_machine_time : 1;
    const norm_tool_setup_time = norms?.norm_tool_setup_time ?? 0;
    return {
        id: modelInfo.material_external_id,
        isDisabled: false,
        materialId: modelInfo.material_external_id,
        materialUuid: material.uuid,
        materialTitle: material.title || "",
        lineId: modelInfo.line_external_id,
        lineUuid: modelInfo.line_uuid,
        lineTitle: modelInfo.line_external_id,
        processNum: modelInfo.process_num,
        modelSpeed: modelInfo.prediction_1h, // parts/h
        modelSetup: modelInfo.intercept, // in hours
        normativeSpeed: norm_base_units / safe_norm_machine_time,
        normativeSetup: norm_tool_setup_time,
        modelFallbackThresholdH: modelInfo.model_fallback_threshold_h,
        skipTooShortOrderFallbackToNormative: modelInfo.skip_too_short_order_fallback_to_normative,
        skipTooOptimisticFallbackToNormative: modelInfo.skip_too_optimistic_fallback_to_normative,
        ignorePredictionModelForLine: modelInfo.ignore_prediction_model_for_line,
        ignorePredictionModelForMaterial: modelInfo.ignore_prediction_model_for_material,
        historicalData: points.data.map(point => ({ quantity: point.x, duration: point.y })),
        latestOperations: evaluatedOperations.map(operation => ({
            operationUuid: operation.operation_uuid,
            orderExternalId: operation.order_external_id,
            actualDateStartTs: operation.actual_start_ts ?? null,
            actualDateEndTs: operation.actual_end_ts ?? null,
            actualDurationH: operation.actual_duration_h || 0,
            quantity: operation.quantity,
            breakTime: 30, // TODO - hardcoded break time
        })),
    };
};

/**
 * Calculates the predicted duration based on a simple linear model.
 * Returns the predicted total duration in hours.
 */
export const calculateDurationH = (
    quantity: number,
    setupTimeHours: number,
    partsPerHour: number,
    breakTimeMin = 0,
    shiftDurationMin = 480
): number => {
    if (partsPerHour <= 0 || quantity < 0) return 0;

    // Calculate break-time ratio and apply it to reduce the effective partsPerHour
    const breakTimeRatio = breakTimeMin / shiftDurationMin;
    const effectiveSpeed = partsPerHour * (1 - breakTimeRatio);

    return setupTimeHours + quantity / effectiveSpeed;
};

/**
 * Calculates the predicted duration based on a simple linear model.
 * Returns the predicted total duration in minutes.
 */
export const calculateDurationMin = (
    quantity: number,
    setupTimeHours: number,
    partsPerHour: number,
    breakTimeMin = 0,
    shiftDurationMin = 480
): number => {
    return calculateDurationH(quantity, setupTimeHours, partsPerHour, breakTimeMin, shiftDurationMin) * 60;
};

/**
 * Calculates the normative break time for a work order based on its duration.
 */
export const calculateNormativeBreakTime = (
    durationMinutes: number,
    breakTimePerShiftMinutes: number,
    shiftDurationMinutes = 480
) => {
    if (breakTimePerShiftMinutes <= 0) return 0;

    // Calculate how many shifts this order spans
    // const shiftsSpanned = Math.ceil(durationMinutes / shiftDurationMinutes);

    // Distribute break time proportionally across the shifts spanned
    return (breakTimePerShiftMinutes / shiftDurationMinutes) * durationMinutes;
};

/**
 * Calculates the break-time correction details for display.
 */
export const calculateBreakTimeCorrection = (breakTimeMin: number, shiftDurationMin = 480) => {
    const breakTimeRatio = breakTimeMin / shiftDurationMin;
    const effectiveWorkTime = shiftDurationMin - breakTimeMin;
    const speedReduction = breakTimeRatio * 100;

    return {
        breakTimeRatio,
        effectiveWorkTime,
        speedReduction,
        breakTimeHours: breakTimeMin / 60,
        shiftDurationHours: shiftDurationMin / 60,
    };
};

/**
 * Format durationMin in hours or minutes
 */
export const formatDuration = (durationMin: number, useHours = false) => {
    if (useHours) {
        return `${niceNumber(durationMin / 60, 2)}h`;
    } else {
        return `${niceNumber(durationMin, 2)} min`;
    }
};
